import { useCallback, useEffect, useRef, useState } from "react";
import {
  eventOf,
  eventPlural,
  eventWords,
  optimiseFor,
  RARE_PER_WEEK,
  VISIT_EVENTS,
} from "../../../shared/goals/events";
import type { MetaForm, MetaSettings } from "../../../shared/goals/meta";
import { Icon } from "../../components/ui/Icon";
import { ApiError } from "../../lib/api";
import { trackingApi, type Tracking } from "../../lib/audienceApi";

/**
 * Where the ads open (notes/AUDIENCE.md 5.6, 8.1, 8.2): Meta's own form, built in place, or a page on the
 * business's website, with "Check it with Meta" (the pixel on the page, then one test enquiry Meta
 * reports), and only once the test passes, the pick of what Meta learns from, among the events the pixel
 * sends. The form learns from its own enquiries.
 */

export const DESTS = [
  {
    key: "metaform",
    icon: "form",
    name: "Meta's form",
    line: "A sign-up form inside Facebook and Instagram, so people never leave the app.",
  },
  {
    key: "site",
    icon: "globe",
    name: "A page on your website",
    line: "Needs the Meta pixel on the page.",
  },
] as const;

type Draft = {
  questions: { q: string; answers: string[] }[];
  privacyUrl: string;
};
const draftOf = (f: MetaForm | undefined): Draft => ({
  questions: (f?.questions ?? []).map((q) => ({
    q: q.q,
    answers: [...q.answers],
  })),
  privacyUrl: f?.privacyUrl ?? "",
});
/** What a draft saves: blank questions and answers left out. */
const clean = (d: Draft): MetaForm => ({
  questions: d.questions
    .filter((q) => q.q.trim())
    .map((q) => ({
      q: q.q.trim(),
      answers: q.answers.map((a) => a.trim()).filter(Boolean),
    })),
  ...(d.privacyUrl.trim() ? { privacyUrl: d.privacyUrl.trim() } : {}),
});
const same = (a: MetaForm | undefined, b: MetaForm) =>
  JSON.stringify(clean(draftOf(a))) === JSON.stringify(b);

export const EVENTS_MANAGER = "https://business.facebook.com/events_manager2";
/** How long the page waits for Meta to report the test enquiry, asking every 10 seconds. */
const TEST_WAIT_MS = 120_000;

const message = (e: unknown) =>
  e instanceof ApiError ? e.message : "Something went wrong. Try again.";

export interface WhereProps {
  goalId: string;
  settings: MetaSettings;
  /** The plan's pick of what Meta learns from. */
  suggested: string | null;
  /** The plan's pick of where the ads open. */
  suggestedOpens: "metaform" | "site" | null;
  link: string;
  setLink: (v: string) => void;
  saveLink: () => void;
  saveSettings: (
    patch: Record<string, unknown>,
    summary: string,
  ) => Promise<void>;
  say: (m: string, ok?: boolean) => void;
}

/** Where the ads open, its checks with Meta and its form: the state and the actions both layouts share. */
export function useWhereOpens({ goalId, settings, saveSettings, say }: WhereProps) {
  const [t, setT] = useState<Tracking | null>(null);
  const [busy, setBusy] = useState<"page" | "test" | null>(null);
  const [waited, setWaited] = useState(false);
  const stop = useRef(false);
  const [draft, setDraft] = useState<Draft>(() => draftOf(settings.form));
  const saved = settings.form;
  const sent = useRef(new Set<string>());
  // Why: a newer version from elsewhere (another tab, the agent) replaces the draft; this page's own saves coming back do not.
  useEffect(() => {
    if (sent.current.has(JSON.stringify(clean(draftOf(saved))))) return;
    setDraft((d) => (same(saved, clean(d)) ? d : draftOf(saved)));
  }, [saved]);
  useEffect(
    () => () => {
      stop.current = true;
    },
    [],
  );

  const load = useCallback(async () => {
    try {
      setT(await trackingApi.get(goalId));
    } catch (e) {
      say(message(e), false);
    }
  }, [goalId, say]);
  useEffect(() => {
    void load();
  }, [load, settings.link]);

  const checkPage = async () => {
    setBusy("page");
    try {
      await trackingApi.checkPage(goalId);
      await load();
    } catch (e) {
      say(message(e), false);
    } finally {
      setBusy(null);
    }
  };
  // Why: Meta reports a pixel's events with a delay; ask every 10 seconds for up to 2 minutes.
  const submitted = async () => {
    setBusy("test");
    setWaited(false);
    const since = new Date(Date.now() - 10 * 60_000).toISOString();
    const until = Date.now() + TEST_WAIT_MS;
    try {
      for (;;) {
        const { test } = await trackingApi.checkTest(goalId, since);
        if (
          test.event ||
          test.visitsOnly ||
          Date.now() > until ||
          stop.current
        ) {
          if (!test.event && !test.visitsOnly) setWaited(true);
          break;
        }
        await new Promise((done) => setTimeout(done, 10_000));
      }
      await load();
    } catch (e) {
      say(message(e), false);
    } finally {
      setBusy(null);
    }
  };

  const check = t?.check && t.check.page === settings.link ? t.check : null;
  const pixel = check?.pixel ?? null;
  const test = check?.test ?? null;
  const step1 = !!pixel?.found || !!test?.event;
  const step2 = !!test?.event;
  const pick = eventOf(settings.optimise);
  const learnable = (t?.events ?? []).filter((e) => !VISIT_EVENTS.has(e.name));
  if (test?.event && !learnable.some((e) => e.name === test.event))
    learnable.unshift({ name: test.event, count: 0 });
  const counted = settings.counts
    ? (t?.events ?? []).find((e) => e.name === settings.counts)
    : null;
  const rare =
    pick === settings.counts && (counted?.count ?? 0) < RARE_PER_WEEK;
  const slow = rare
    ? `${eventPlural(pick)} are rare, so Meta will learn slowly at first.`
    : null;
  const learn = (name: string) =>
    void saveSettings(
      { optimise: optimiseFor(name) },
      `What Meta aims for: ${eventPlural(name)}`,
    );
  const choose = (key: "metaform" | "site") => {
    if (key !== settings.opens)
      void saveSettings(
        { opens: key },
        key === "metaform"
          ? "Where the ads open: Meta's form"
          : "Where the ads open: a page on your website",
      );
  };
  const saveForm = (d: Draft) => {
    const next = clean(d);
    if (same(saved, next)) return;
    if (next.privacyUrl && !/^https:\/\/[^\s/]+\.[^\s]+/.test(next.privacyUrl))
      return say("The privacy policy address starts with https://", false);
    sent.current.add(JSON.stringify(next));
    void saveSettings({ form: next }, "The form changed");
  };
  const put = (f: (d: Draft) => Draft, save = false) => {
    const n = f(draft);
    setDraft(n);
    if (save) saveForm(n);
  };
  const setQ =
    (
      qi: number,
      f: (q: Draft["questions"][number]) => Draft["questions"][number] | null,
    ) =>
    (d: Draft): Draft => ({
      ...d,
      questions: d.questions.flatMap((q, i) => (i === qi ? (f(q) ?? []) : [q])),
    });

  return { t, busy, waited, draft, put, setQ, saveForm, checkPage, submitted, pixel, test, step1, step2, pick, learnable, counted, slow, learn, choose };
}

export function WhereOpens(props: WhereProps) {
  const { suggested, suggestedOpens, link, setLink, saveLink, settings } = props;
  const { busy, waited, draft, put, setQ, saveForm, checkPage, submitted, pixel, test, step1, step2, pick, learnable, counted, slow, learn, choose, t } = useWhereOpens(props);

  return (
    <section className="card" id="y-dest" style={{ marginTop: 16 }}>
      <div className="ysec-h">
        <h2>Where the ads open</h2>
      </div>
      <div className="dests" role="radiogroup" aria-label="Where the ads open">
        {DESTS.map((d) => {
          const on = settings.opens === d.key;
          return (
            <div
              key={d.key}
              className={`dest${on ? " on" : ""}`}
              role="radio"
              aria-checked={on}
              tabIndex={on ? -1 : 0}
              onClick={on ? undefined : () => choose(d.key)}
              onKeyDown={
                on
                  ? undefined
                  : (e) => {
                      if (e.key !== "Enter" && e.key !== " ") return;
                      e.preventDefault();
                      choose(d.key);
                    }
              }
            >
              <span className="dot" />
              <span className="ki">
                <Icon name={d.icon} />
              </span>
              <div>
                <b>{d.name}</b>
                {d.key === suggestedOpens && (
                  <span className="rec">Suggested</span>
                )}
                <p>{d.line}</p>
                {on && d.key === "metaform" && (
                  <>
                    <div className="fq">
                      <span className="afl">
                        It asks for name, phone and email, and:
                      </span>
                      {draft.questions.map((x, qi) => (
                        <div className="fqq" key={qi}>
                          <div className="fqq-h">
                            <input
                              value={x.q}
                              aria-label={`Question ${qi + 1}`}
                              onChange={(e) =>
                                put(
                                  setQ(qi, (q) => ({
                                    ...q,
                                    q: e.target.value,
                                  })),
                                )
                              }
                              onBlur={() => saveForm(draft)}
                            />
                            <button
                              className="fx"
                              type="button"
                              aria-label="Remove this question"
                              onClick={() =>
                                put(
                                  setQ(qi, () => null),
                                  true,
                                )
                              }
                            >
                              <Icon name="x" />
                            </button>
                          </div>
                          <div className="fq-a">
                            {x.answers.map((ans, ai) => (
                              <span className="fa" key={ai}>
                                <input
                                  value={ans}
                                  size={Math.max(6, ans.length)}
                                  aria-label="An answer"
                                  onChange={(e) =>
                                    put(
                                      setQ(qi, (q) => ({
                                        ...q,
                                        answers: q.answers.map((v, k) =>
                                          k === ai ? e.target.value : v,
                                        ),
                                      })),
                                    )
                                  }
                                  onBlur={() => saveForm(draft)}
                                />
                                <button
                                  className="fx"
                                  type="button"
                                  aria-label="Remove this answer"
                                  onClick={() =>
                                    put(
                                      setQ(qi, (q) => ({
                                        ...q,
                                        answers: q.answers.filter(
                                          (_, k) => k !== ai,
                                        ),
                                      })),
                                      true,
                                    )
                                  }
                                >
                                  <Icon name="x" />
                                </button>
                              </span>
                            ))}
                            <button
                              className="add"
                              type="button"
                              onClick={() =>
                                put(
                                  setQ(qi, (q) => ({
                                    ...q,
                                    answers: [...q.answers, ""],
                                  })),
                                )
                              }
                            >
                              <Icon name="plus" />
                              Add an answer
                            </button>
                          </div>
                          {!x.answers.length && (
                            <p className="hint">They type their answer.</p>
                          )}
                        </div>
                      ))}
                      {draft.questions.length < 5 && (
                        <button
                          className="add"
                          type="button"
                          onClick={() =>
                            put((f) => ({
                              ...f,
                              questions: [
                                ...f.questions,
                                { q: "", answers: [] },
                              ],
                            }))
                          }
                        >
                          <Icon name="plus" />
                          Add a question
                        </button>
                      )}
                      <label className="urlf">
                        <span className="afl">Your privacy policy</span>
                        <input
                          value={draft.privacyUrl}
                          placeholder="https://"
                          onChange={(e) =>
                            put((f) => ({ ...f, privacyUrl: e.target.value }))
                          }
                          onBlur={() => saveForm(draft)}
                        />
                      </label>
                      <p className="hint">
                        Sign-ups arrive in Meta's Leads Center, in Meta
                        Business Suite.
                      </p>
                    </div>
                    <div className="learnin">
                      <span className="afl">What Meta aims for</span>
                      <div className="evp on fixed">
                        <span className="dot" />
                        <span className="t">
                          <b>Sign-ups on this form</b>
                          <span className="g">Someone submitted this form</span>
                        </span>
                      </div>
                    </div>
                  </>
                )}
                {on && d.key === "site" && (
                  <>
                    <label className="urlf">
                      <span className="afl">The page with your form</span>
                      <input
                        value={link}
                        placeholder="https://"
                        onChange={(e) => setLink(e.target.value)}
                        onBlur={saveLink}
                      />
                      <span className="hint">
                        Landing here is only a visit. What counts is what they
                        do next, like submitting the form.
                      </span>
                    </label>
                    {settings.link && (
                      <div className="learnin">
                        <span className="afl">Check it with Meta</span>
                        <ol className="scl">
                          <li className={`scs${step1 ? " ok" : " cur"}`}>
                            <span className="scn">
                              {step1 ? <Icon name="check" /> : 1}
                            </span>
                            <div>
                              <b>The pixel on this page</b>
                              <div className="scin">
                                <button
                                  className="btn sm"
                                  type="button"
                                  disabled={busy !== null}
                                  onClick={() => void checkPage()}
                                >
                                  {busy === "page" && <span className="spin" />}
                                  Check the page
                                </button>
                              </div>
                              {pixel?.found && (
                                <p className="okl">
                                  Pixel found: {pixel.pixelName ?? "your pixel"}
                                  .
                                </p>
                              )}
                              {pixel && !pixel.found && pixel.other && (
                                <p className="warnl">
                                  This page has another pixel (
                                  <code>{pixel.other}</code>), not{" "}
                                  {pixel.pixelName ?? "yours"}.
                                </p>
                              )}
                              {pixel &&
                                !pixel.found &&
                                !pixel.other &&
                                !test?.event && (
                                  <p className="warnl">
                                    {pixel.error ??
                                      `Not in the page itself${pixel.tagManager ? ", which loads a tag manager" : ""}. A plugin or a tag manager may load it: the test sign-up is the proof.`}
                                  </p>
                                )}
                            </div>
                          </li>
                          <li
                            className={`scs${step2 ? " ok" : pixel ? " cur" : ""}`}
                          >
                            <span className="scn">
                              {step2 ? <Icon name="check" /> : 2}
                            </span>
                            <div>
                              <b>Submit one test sign-up</b>
                              {pixel && (
                                <>
                                  <p className="hint">
                                    Fill in the form on the page and submit it.
                                  </p>
                                  <div className="scin">
                                    <a
                                      className="btn sm ghost"
                                      href={settings.link}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      Open the page
                                    </a>
                                    <button
                                      className="btn sm"
                                      type="button"
                                      disabled={busy !== null}
                                      onClick={() => void submitted()}
                                    >
                                      {busy === "test" ? (
                                        <>
                                          <span className="spin" />
                                          Waiting for Meta to report it
                                        </>
                                      ) : (
                                        "I submitted it"
                                      )}
                                    </button>
                                  </div>
                                  {test?.event && (
                                    <p className="okl">
                                      Meta got it, as <code>{test.event}</code>.
                                    </p>
                                  )}
                                  {test?.visitsOnly && !test.event && (
                                    <p className="warnl">
                                      Meta saw the visit, but not the sign-up (<code>Lead</code>). Add it (below), then submit
                                      again.
                                    </p>
                                  )}
                                  {waited &&
                                    !test?.event &&
                                    !test?.visitsOnly && (
                                      <p className="warnl">
                                        Meta has not reported it yet. In Meta
                                        Events Manager, select your pixel, then{" "}
                                        <b>Test events</b>, to see it arrive.{" "}
                                        <a
                                          href={EVENTS_MANAGER}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                        >
                                          Open Events Manager
                                        </a>
                                      </p>
                                    )}
                                </>
                              )}
                            </div>
                          </li>
                        </ol>
                      </div>
                    )}
                    {test?.visitsOnly && !test.event && (
                      <div className="learnin">
                        <span className="afl">What Meta aims for</span>
                        <div className="seen miss">
                          <Icon name="info" />
                          <div>
                            <b>Meta only sees visits to your site.</b>
                            <p>
                              It can't tell when someone submits your form. In
                              Meta Events Manager, select your pixel, then{" "}
                              <b>Settings</b> and <b>Open Event Setup Tool</b>.
                              Choose <b>Track new button</b>, click your form's
                              submit button, and pick <b>Lead</b>. No code
                              needed.
                            </p>
                            <p>
                              If your form goes to a thank-you page, choose{" "}
                              <b>Track a URL</b> for that page instead.
                            </p>
                            <a
                              className="btn sm"
                              href={EVENTS_MANAGER}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Open Events Manager
                            </a>
                          </div>
                        </div>
                      </div>
                    )}
                    {step2 && (
                      <div className="learnin">
                        <span className="afl">What Meta aims for</span>
                        <p className="hint">
                          Pick the event from your pixel that matches your goal.
                        </p>
                        <div
                          className="evpick"
                          role="radiogroup"
                          aria-label="What Meta aims for"
                        >
                          {learnable.map((e) => (
                            <button
                              key={e.name}
                              type="button"
                              role="radio"
                              aria-checked={e.name === pick}
                              className={`evp${e.name === pick ? " on" : ""}`}
                              onClick={() => e.name !== pick && learn(e.name)}
                            >
                              <span className="dot" />
                              <span className="t">
                                <b>{eventWords(e.name)}</b>
                                <span className="g">{e.name}</span>
                              </span>
                              {e.name === suggested && (
                                <span className="rec">Suggested</span>
                              )}
                            </button>
                          ))}
                        </div>
                        {test?.event && pick !== test.event ? (
                          <div className="sure">
                            <Icon name="info" />
                            <p>
                              Your test arrived as {eventPlural(test.event).toLowerCase()}, not {eventPlural(pick).toLowerCase()}. Sure Meta should aim for {eventPlural(pick).toLowerCase()}?
                              {slow ? ` ${slow}` : ""}
                            </p>
                            <button
                              className="btn sm"
                              type="button"
                              onClick={() => learn(test.event!)}
                            >
                              Use {eventPlural(test.event).toLowerCase()}
                            </button>
                          </div>
                        ) : (
                          slow && <p className="why">{slow}</p>
                        )}
                      </div>
                    )}
                    {settings.counts &&
                      t?.events &&
                      !counted &&
                      !test?.visitsOnly && (
                        <div className="seen miss">
                          <Icon name="info" />
                          <div>
                            <b>
                              Meta can't see your{" "}
                              {eventPlural(settings.counts).toLowerCase()} yet.
                            </b>
                            <p>
                              {settings.counts === "Schedule"
                                ? "Your booking tool"
                                : "The tool that records them"}{" "}
                              can send them to Meta, or you can upload them in
                              Meta Events Manager.
                            </p>
                            <a
                              className="btn sm"
                              href={EVENTS_MANAGER}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Open Events Manager
                            </a>
                          </div>
                        </div>
                      )}
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
